import React, { useState, useEffect } from 'react';
import { Download, FileText, Video, BookOpen, Search, Filter, ArrowLeft, Image, Play, FolderOpen } from 'lucide-react';
import { useData } from '../context/DataContext';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get correct URL (handles both Cloudinary and local URLs)
const getFileUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const ResourcesSection = ({ setActiveTab }) => {
  const { resources, loading } = useData();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);

  // Fetch uploaded resource files
  useEffect(() => {
    const fetchFiles = async () => {
      setFilesLoading(true);
      try {
        const files = await api.getFilesByCategory('resources');
        setUploadedFiles(files);
      } catch (err) {
        console.error('Error fetching resource files:', err);
        setUploadedFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };
    fetchFiles();
  }, []);

  const filters = [
    { id: 'all', label: 'All Resources', icon: BookOpen },
    { id: 'lesson-plan', label: 'Lesson Plans', icon: FileText },
    { id: 'video', label: 'Videos', icon: Video },
  ];

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video className="w-6 h-6" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const getResourceColor = (type) => {
    switch (type) {
      case 'video':
        return 'bg-[#2c5282] text-white';
      default:
        return 'bg-[#1e3a5f] text-white';
    }
  };

  const filteredResources = (resources || []).filter((resource) => {
    const matchesFilter = activeFilter === 'all' || resource.type === activeFilter;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Filter uploaded files based on search and active filter
  const filteredUploadedFiles = uploadedFiles.filter((file) => {
    const matchesSearch = (file.title || file.original_name).toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (file.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === 'all' || 
                          (activeFilter === 'video' && file.file_type === 'video') ||
                          (activeFilter === 'lesson-plan' && file.file_type === 'document');
    return matchesSearch && matchesFilter;
  });

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-6 h-6" />;
      case 'video': return <Play className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <section className="min-h-[calc(100vh-80px)] py-12 bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#1e3a5f] border-t-transparent rounded-full"></div>
      </section>
    );
  }

  return (
    <section className="min-h-[calc(100vh-80px)] py-12 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => setActiveTab('home')}
          className="flex items-center gap-2 text-[#3d6a99] hover:text-[#1e3a5f] mb-8 transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium mb-4">
            Resources
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Educational Materials
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Access our collection of lesson plans, guides, and video content for conflict management education.
          </p>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          {/* Search */}
          <div className="relative flex-grow max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-[#e2e8f0] rounded-xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2c5282]/30 focus:border-[#2c5282] transition-all duration-300"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-400 hidden sm:block" />
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  activeFilter === filter.id
                    ? 'bg-[#1e3a5f] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-[#e2e8f0]'
                }`}
              >
                <filter.icon className="w-4 h-4" />
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => (
            <div
              key={resource.id}
              className="group bg-white rounded-2xl border border-[#e2e8f0] shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden hover:-translate-y-1"
            >
              <div className="p-6">
                {/* Icon & Format */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${getResourceColor(resource.type)} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    {getResourceIcon(resource.type)}
                  </div>
                  <span className="px-3 py-1 bg-[#f0f7ff] text-[#1e3a5f] rounded-full text-xs font-medium">
                    {resource.format}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-[#1e3a5f] mb-2 group-hover:text-[#2c5282] transition-colors duration-300 line-clamp-2">
                  {resource.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                  {resource.description}
                </p>

                {/* Language Badge */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Language: {resource.language}
                  </span>
                  
                  {/* Download Button */}
                  {resource.download_url && resource.download_url !== '#' ? (
                    <a
                      href={resource.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#f0f7ff] text-[#1e3a5f] rounded-lg text-sm font-medium hover:bg-[#1e3a5f] hover:text-white transition-all duration-300"
                    >
                      <Download className="w-4 h-4" />
                      {resource.type === 'video' ? 'Watch' : 'Download'}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Coming soon</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Uploaded Files Section */}
        {filteredUploadedFiles.length > 0 && (
          <div className="mt-12" data-testid="uploaded-resources-section">
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
              <FolderOpen className="w-6 h-6" />
              Uploaded Materials
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredUploadedFiles.map((file) => (
                <a
                  key={file.id}
                  href={getFileUrl(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-xl border border-[#e2e8f0] overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`resource-file-${file.id}`}
                >
                  {/* Preview */}
                  {file.file_type === 'image' ? (
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      <img 
                        src={getFileUrl(file.url)} 
                        alt={file.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <div className={`aspect-video flex items-center justify-center ${
                      file.file_type === 'video' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        file.file_type === 'video' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {getFileIcon(file.file_type)}
                      </div>
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="p-4">
                    <h4 className="font-medium text-[#1e3a5f] truncate group-hover:text-[#2c5282] transition-colors">
                      {file.title || file.original_name}
                    </h4>
                    {file.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{file.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400 uppercase">{file.file_type}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-[#1e3a5f] font-medium">
                        <Download className="w-3 h-3" />
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredResources.length === 0 && filteredUploadedFiles.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-[#f0f7ff] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-[#3d6a99]" />
            </div>
            <p className="text-gray-600">No resources found matching your criteria.</p>
            <button
              onClick={() => { setActiveFilter('all'); setSearchTerm(''); }}
              className="mt-4 text-[#2c5282] hover:text-[#1e3a5f] font-medium"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Coming Soon Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            More resources will be added as the project progresses. Check back regularly!
          </p>
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;
